import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { defaultEducation, educationSchema } from "@reactive-resume/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { getCoachMetadata } from "@/client/libs/coach-metadata";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { MonthYearDatePicker } from "../sections/shared/month-year-date-picker";
import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";
import { YearDatePicker } from "../sections/shared/year-date-picker";

const formSchema = educationSchema;
type FormValues = z.infer<typeof formSchema>;

export const EducationDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultEducation,
    resolver: zodResolver(formSchema),
  });

  const [storedProgramName, setStoredProgramName] = useState("");
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const educationItemsCount = useResumeStore(
    (state) => state.resume.data?.sections?.education?.items?.length ?? 0,
  );
  const isMastersUnion = template === "mastersunion";
  const { isOpen, mode } = useDialog<FormValues>("education");

  useEffect(() => {
    if (!isOpen || mode !== "create" || !isMastersUnion || educationItemsCount > 0) return;

    const { organizationName, programName } = getCoachMetadata();
    setStoredProgramName(programName);

    form.setValue(
      "institution",
      { label: organizationName || "", href: "" },
      { shouldValidate: true },
    );
    form.setValue("program", programName, { shouldValidate: true });
  }, [educationItemsCount, form, isMastersUnion, isOpen, mode]);

  return (
    <SectionDialog<FormValues> id="education" form={form} defaultValues={defaultEducation}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="institution"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Institution`}</FormLabel>
              <FormControl>
                <URLInput
                  {...field}
                  value={
                    typeof field.value === "string"
                      ? { label: field.value, href: "" }
                      : field.value
                  }
                  placeholder="Institution Name"
                  labelPlaceholder="Website"
                  disableAutoHttps={true}
                  swapLabelAndHref={true}
                  onChange={(value) => field.onChange(value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isMastersUnion && (
          <FormField
            name="program"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Course Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={storedProgramName || "Course Name"}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!isMastersUnion && <FormField
          name="studyType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Course Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="B.Tech, MBA, PGDM"/>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />}

        <FormField
          name="area"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t({
                  message: "Location",
                  comment: "For example, Computer Science or Business Administration",
                })}
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Location" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isMastersUnion && (
          <>
            <FormField
              name="score"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t({
                      message: "Score",
                      comment: "Score or honors for the degree, for example, CGPA or magna cum laude",
                    })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="9.2 GPA" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="url"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Website`}</FormLabel>
                  <FormControl>
                    <URLInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="highlights"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Education Highlights</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Date or Date Range`}</FormLabel>
              <FormControl>
                {/* Masters' Union resumes show month + year for education; other templates stay year-only */}
                {isMastersUnion ? (
                  <MonthYearDatePicker value={field.value} onChange={field.onChange} />
                ) : (
                  <YearDatePicker value={field.value} onChange={field.onChange} />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Summary`}</FormLabel>
              <FormControl>
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    // footer={(editor) => (
                    //   <AiActions
                    //     value={editor.getText()}
                    //     content={editor.getHTML()}
                    //     onChange={(value) => {
                    //       editor.commands.setContent(value);
                    //       field.onChange(value);
                    //     }}
                    //   />
                    // )}
                  />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};